import React, { useEffect, useState } from "react";
import { useNavigate, Link, Routes, Route } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatINR, imgUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { LayoutDashboard, Package, ShoppingBag, LogOut, Home, Trash2, Pencil, Upload, X, GripVertical, Layers, Eye, EyeOff, Tag, Settings } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

function Sidebar() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const items = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/categories", label: "Categories", icon: Layers },
    { to: "/admin/sales", label: "Sales", icon: Tag },
    { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ];
  return (
    <aside className="w-64 bg-[#2b1b1c] text-[#fcfbf8] min-h-screen p-6 flex flex-col" data-testid="admin-sidebar">
      <div className="mb-10"><Logo variant="light" /></div>
      <nav className="flex-1 space-y-1">
        {items.map((i) => (
          <Link key={i.to} to={i.to} className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-[#7a1e2b] transition-colors rounded-sm" data-testid={`admin-nav-${i.label.toLowerCase()}`}>
            <i.icon size={16} /> {i.label}
          </Link>
        ))}
      </nav>
      <div className="space-y-1 pt-6 border-t border-[#7a1e2b]/40">
        <Link to="/" className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-[#7a1e2b] rounded-sm"><Home size={16} /> Storefront</Link>
        <button onClick={() => { logout(); nav("/"); }} className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-[#7a1e2b] rounded-sm w-full" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
      </div>
    </aside>
  );
}

function EmailTester() {
  const { user } = useAuth();
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (user?.email) setTo(user.email); }, [user]);

  const send = async () => {
    if (!to) return toast.error("Enter a recipient email");
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { to });
      setResult(data);
      if (data.ok) toast.success("Test email dispatched");
      else toast.error("Test email failed — see details");
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setResult({ ok: false, reason: detail });
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#e8e4db]">
      <div className="flex gap-2">
        <Input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="Recipient email"
          className="text-xs h-8"
          data-testid="email-test-to"
        />
        <button
          onClick={send}
          disabled={sending}
          className="text-xs px-3 py-1 bg-[#7a1e2b] text-white hover:bg-[#5c1520] disabled:opacity-50 whitespace-nowrap"
          data-testid="email-test-send"
        >{sending ? "Sending…" : "Send Test"}</button>
      </div>
      {result && (
        <div className={`mt-2 p-2 text-xs ${result.ok ? "bg-[#4a7a59]/10 text-[#4a7a59]" : "bg-[#b33939]/10 text-[#b33939]"}`} data-testid="email-test-result">
          {result.ok ? (
            <>✓ Email sent to {result.to} · ID: <code>{result.email_id}</code></>
          ) : (
            <>
              <div className="font-medium">✗ {result.reason}</div>
              {result.hint && <div className="text-[#2b1b1c] mt-1"><strong>Fix:</strong> {result.hint}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stats() {
  const [s, setS] = useState(null);
  const [sys, setSys] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
    api.get("/admin/system-status").then((r) => setSys(r.data)).catch(() => setSys({ error: true }));
  }, []);
  if (!s) return <div className="text-[#7c7273]">Loading…</div>;
  const cards = [
    { label: "Revenue", value: formatINR(s.revenue), color: "#7a1e2b" },
    { label: "Orders", value: s.total_orders, color: "#c9a34a" },
    { label: "Products", value: s.total_products, color: "#4a7a59" },
    { label: "Customers", value: s.total_users, color: "#2b1b1c" },
  ];
  return (
    <div>
      <h1 className="font-serif text-3xl text-[#2b1b1c] mb-2">Dashboard</h1>
      <p className="text-[#7c7273] mb-8">Overview of your atelier.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-[#e8e4db] p-6" data-testid={`stat-${c.label.toLowerCase()}`}>
            <div className="text-xs uppercase tracking-widest text-[#7c7273]">{c.label}</div>
            <div className="font-serif text-3xl mt-2" style={{ color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {sys && !sys.error && (
        <div className="bg-white border border-[#e8e4db] p-6" data-testid="admin-system-status">
          <h3 className="font-serif text-xl text-[#2b1b1c] mb-4">System Status</h3>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div className={`border p-4 ${sys.razorpay.configured && sys.razorpay.live_test?.ok ? "border-[#4a7a59] bg-[#4a7a59]/5" : "border-[#b33939] bg-[#b33939]/5"}`}>
              <div className="text-xs uppercase tracking-widest text-[#7c7273] mb-2">Razorpay</div>
              <div className="font-medium">
                {sys.razorpay.configured ? (
                  sys.razorpay.live_test?.ok ? "✓ Live keys active" : "⚠ Keys configured but API failed"
                ) : "✗ Not configured (DEMO mode)"}
              </div>
              {sys.razorpay.configured && (
                <div className="text-xs text-[#7c7273] mt-2 font-mono">
                  Key: {sys.razorpay.key_id_prefix}…{sys.razorpay.key_id_suffix} · Secret len: {sys.razorpay.secret_length}
                </div>
              )}
              {sys.razorpay.live_test?.detail && (
                <div className="text-xs text-[#7c7273] mt-1">{sys.razorpay.live_test.detail}</div>
              )}
              {!sys.razorpay.configured && (
                <div className="text-xs text-[#7c7273] mt-2">
                  Set <code className="bg-[#f5f3ef] px-1">RAZORPAY_KEY_ID</code> and <code className="bg-[#f5f3ef] px-1">RAZORPAY_KEY_SECRET</code> in your Emergent deployment environment settings, then redeploy.
                </div>
              )}
            </div>

            <div className={`border p-4 ${sys.storage.configured ? "border-[#4a7a59] bg-[#4a7a59]/5" : "border-[#b33939] bg-[#b33939]/5"}`}>
              <div className="text-xs uppercase tracking-widest text-[#7c7273] mb-2">Object Storage (uploads)</div>
              <div className="font-medium">
                {sys.storage.configured ? "✓ Configured" : "✗ Missing EMERGENT_LLM_KEY"}
              </div>
              <div className="text-xs text-[#7c7273] mt-2">Session active: {sys.storage.session_active ? "yes" : "no"}</div>
            </div>

            <div className={`border p-4 ${sys.email?.configured ? "border-[#4a7a59] bg-[#4a7a59]/5" : "border-[#b33939] bg-[#b33939]/5"}`}>
              <div className="text-xs uppercase tracking-widest text-[#7c7273] mb-2">Email (Resend)</div>
              <div className="font-medium">
                {sys.email?.configured ? "✓ Configured" : "✗ Missing RESEND_API_KEY"}
              </div>
              {sys.email?.from_email && (
                <div className="text-xs text-[#7c7273] mt-2 font-mono break-all">From: {sys.email.from_email}</div>
              )}
              {sys.email?.note && (
                <div className="text-xs text-[#7c7273] mt-1">{sys.email.note}</div>
              )}
              <EmailTester />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CATEGORIES = ["Banarasi", "Kanjivaram", "Silk", "Chiffon", "Georgette", "Cotton"];
const AVAILABLE_SIZES = ["Free Size", "XS", "S", "M", "L", "XL"];
const EMPTY_PRODUCT = { name: "", category: "Banarasi", fabric: "Silk", color: "", price: 0, compare_at_price: 0, description: "", images: "", video_url: "", sizes: [], stock: 10, featured: false, new_arrival: false, variants: [] };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toFullUrl(u) {
  if (!u) return u;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function ImageUploader({ value, onChange }) {
  const urls = React.useMemo(
    () => (value || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    [value]
  );
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef(null);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newUrls = [...urls];
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name}: not an image`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: exceeds 10 MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(data.url);
        toast.success(`Uploaded ${file.name}`);
      }
      onChange(newUrls.join("\n"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (i) => {
    const next = urls.filter((_, idx) => idx !== i);
    onChange(next.join("\n"));
  };

  const move = (from, to) => {
    if (to < 0 || to >= urls.length) return;
    const next = [...urls];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next.join("\n"));
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          uploadFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[#7a1e2b] bg-[#f5f3ef]" : "border-[#e8e4db] hover:border-[#c9a34a]"}`}
        data-testid="pf-image-dropzone"
      >
        <Upload size={22} className="mx-auto text-[#c9a34a] mb-2" />
        <div className="text-sm font-medium text-[#2b1b1c]">
          {uploading ? "Uploading…" : "Drop saree photos here or click to browse"}
        </div>
        <div className="text-xs text-[#7c7273] mt-1">JPEG, PNG, WebP up to 10 MB · multiple allowed</div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="pf-file-input"
        />
      </div>

      {urls.length > 0 && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="pf-image-grid">
          {urls.map((u, i) => (
            <div key={u + i} className="relative group border border-[#e8e4db] bg-[#f5f3ef]">
              <img
                src={toFullUrl(u)}
                alt={`img-${i}`}
                className="w-full aspect-[3/4] object-cover"
                onError={(e) => { e.target.style.opacity = 0.2; }}
              />
              {i === 0 && (
                <span className="absolute top-1 left-1 bg-[#7a1e2b] text-white text-[9px] tracking-widest uppercase px-1.5 py-0.5">Cover</span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={(e) => { e.stopPropagation(); move(i, i - 1); }} className="text-white text-xs px-2 py-1 hover:bg-black/40" title="Move left">◀</button>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeAt(i); }} className="text-white text-xs px-2 py-1 hover:bg-black/40" title="Remove" data-testid={`pf-image-remove-${i}`}>
                  <X size={12} />
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); move(i, i + 1); }} className="text-white text-xs px-2 py-1 hover:bg-black/40" title="Move right">▶</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste image URLs (one per line)"
        rows={2}
        className="mt-3 font-mono text-[11px]"
        data-testid="pf-images"
      />
    </div>
  );
}

function VideoUploader({ value, onChange }) {
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  const upload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) return toast.error("Not a video file");
    if (file.size > 100 * 1024 * 1024) return toast.error("Video exceeds 100 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {value ? (
        <div className="relative">
          <video src={toFullUrl(value)} controls className="w-full max-h-64 bg-black" data-testid="pf-video-preview" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="mt-2 text-xs text-[#7a1e2b] link-underline"
            data-testid="pf-video-remove"
          >Remove video</button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
          className="border-2 border-dashed border-[#e8e4db] hover:border-[#c9a34a] p-6 text-center cursor-pointer transition-colors"
          data-testid="pf-video-dropzone"
        >
          <Upload size={20} className="mx-auto text-[#c9a34a] mb-2" />
          <div className="text-sm font-medium">{uploading ? "Uploading…" : "Drop a saree drape video (optional)"}</div>
          <div className="text-xs text-[#7c7273] mt-1">MP4 / WebM / MOV up to 100 MB</div>
          <input
            ref={inputRef}
            type="file"
            accept="video/*"
            hidden
            onChange={(e) => upload(e.target.files)}
            data-testid="pf-video-input"
          />
        </div>
      )}
    </div>
  );
}

function VariantImagesUploader({ value, onChange, testIdPrefix }) {
  const urls = value || [];
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const next = [...urls];
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) { toast.error(`${file.name}: not an image`); continue; }
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: exceeds 10 MB`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(data.url);
      }
      onChange(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (i) => onChange(urls.filter((_, idx) => idx !== i));

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-[#e8e4db] hover:border-[#c9a34a] cursor-pointer p-3 text-center text-xs text-[#7c7273]"
        data-testid={`${testIdPrefix}-dropzone`}
      >
        <Upload size={16} className="inline mr-1 text-[#c9a34a]" />
        {uploading ? "Uploading…" : "Add colour photos"}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadFiles(e.target.files)} />
      {urls.length > 0 && (
        <div className="mt-2 grid grid-cols-4 gap-2">
          {urls.map((u, i) => (
            <div key={u + i} className="relative group border border-[#e8e4db] bg-[#f5f3ef]">
              <img src={toFullUrl(u)} alt="" className="w-full aspect-square object-cover" onError={(e) => { e.target.style.opacity = 0.2; }} />
              <button type="button" onClick={() => removeAt(i)} className="absolute top-0.5 right-0.5 bg-black/60 text-white p-0.5 opacity-0 group-hover:opacity-100" title="Remove">
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VariantsEditor({ value, onChange }) {
  const variants = value || [];

  const add = () => {
    const next = [
      ...variants,
      { variant_id: `var_${Math.random().toString(36).slice(2, 12)}`, color: "", color_hex: "#c9a34a", images: [], stock: 5, price: null },
    ];
    onChange(next);
  };

  const update = (i, patch) => {
    const next = variants.map((v, idx) => (idx === i ? { ...v, ...patch } : v));
    onChange(next);
  };

  const remove = (i) => {
    if (!window.confirm("Remove this colour variant?")) return;
    onChange(variants.filter((_, idx) => idx !== i));
  };

  return (
    <div data-testid="pf-variants">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-[#7c7273]">Add each colour this saree comes in. Leave empty if you sell only one colour.</div>
        <button type="button" onClick={add} className="text-xs bg-[#7a1e2b] text-white px-3 py-1.5 hover:bg-[#5c1520]" data-testid="pf-variant-add">+ Add colour</button>
      </div>

      {variants.length === 0 ? (
        <div className="text-center text-xs text-[#7c7273] py-4 italic border border-dashed border-[#e8e4db]">No colour variants yet.</div>
      ) : (
        <ul className="space-y-3">
          {variants.map((v, i) => (
            <li key={v.variant_id || i} className="border border-[#e8e4db] bg-[#faf8f4] p-3" data-testid={`pf-variant-row-${i}`}>
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-2 flex items-center gap-2">
                  <input
                    type="color"
                    value={v.color_hex || "#c9a34a"}
                    onChange={(e) => update(i, { color_hex: e.target.value })}
                    className="w-8 h-8 border border-[#e8e4db] cursor-pointer"
                    data-testid={`pf-variant-hex-${i}`}
                  />
                  <Input
                    placeholder="Colour name"
                    value={v.color}
                    onChange={(e) => update(i, { color: e.target.value })}
                    className="h-8 text-sm"
                    data-testid={`pf-variant-color-${i}`}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    min="0"
                    value={v.stock}
                    onChange={(e) => update(i, { stock: parseInt(e.target.value || "0", 10) })}
                    className="h-8 text-sm"
                    placeholder="Stock"
                    data-testid={`pf-variant-stock-${i}`}
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={v.price ?? ""}
                    onChange={(e) => update(i, { price: e.target.value === "" ? null : parseFloat(e.target.value) })}
                    className="h-8 text-sm"
                    placeholder="Price (optional)"
                    data-testid={`pf-variant-price-${i}`}
                  />
                </div>
                <div className="col-span-4">
                  <VariantImagesUploader
                    value={v.images || []}
                    onChange={(imgs) => update(i, { images: imgs })}
                    testIdPrefix={`pf-variant-images-${i}`}
                  />
                </div>
                <div className="col-span-1 text-right">
                  <button type="button" onClick={() => remove(i)} className="text-[#7a1e2b] hover:text-[#5c1520] p-1" title="Remove" data-testid={`pf-variant-remove-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProductForm({ value, onChange }) {
  const on = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const onNum = (k) => (e) => onChange({ ...value, [k]: e.target.value === "" ? 0 : parseFloat(e.target.value) });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Product name</Label>
        <Input placeholder="e.g. Ruby Banarasi Silk" value={value.name} onChange={on("name")} required data-testid="pf-name" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Category</Label>
        <Select value={value.category} onValueChange={(v) => onChange({ ...value, category: v })}>
          <SelectTrigger data-testid="pf-category"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Fabric</Label>
        <Input value={value.fabric} onChange={on("fabric")} required data-testid="pf-fabric" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Default colour</Label>
        <Input value={value.color} onChange={on("color")} required data-testid="pf-color" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Base stock (used when no colour variants)</Label>
        <Input type="number" min="0" value={value.stock} onChange={(e) => onChange({ ...value, stock: parseInt(e.target.value || "0", 10) })} data-testid="pf-stock" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Price (₹)</Label>
        <Input type="number" min="0" step="0.01" value={value.price} onChange={onNum("price")} required data-testid="pf-price" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Compare-at price (₹, optional)</Label>
        <Input type="number" min="0" step="0.01" value={value.compare_at_price || ""} onChange={onNum("compare_at_price")} data-testid="pf-compare-price" />
      </div>
      <div className="sm:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Default Product Images (shown when no variant is selected)</Label>
        <ImageUploader
          value={value.images}
          onChange={(v) => onChange({ ...value, images: v })}
        />
      </div>
      <div className="sm:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Product Video (optional)</Label>
        <VideoUploader
          value={value.video_url}
          onChange={(v) => onChange({ ...value, video_url: v })}
        />
      </div>
      <div className="sm:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Colour Variants</Label>
        <VariantsEditor value={value.variants} onChange={(v) => onChange({ ...value, variants: v })} />
      </div>
      <div className="sm:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-[#7c7273]">Description</Label>
        <Textarea value={value.description} onChange={on("description")} rows={3} required data-testid="pf-description" />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={value.featured} onCheckedChange={(v) => onChange({ ...value, featured: v })} data-testid="pf-featured" />
        <Label>Featured on homepage</Label>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={value.new_arrival} onCheckedChange={(v) => onChange({ ...value, new_arrival: v })} data-testid="pf-new" />
        <Label>New arrival</Label>
      </div>
    </div>
  );
}

function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null); // product being edited
  const [form, setForm] = useState(EMPTY_PRODUCT);

  const load = () => api.get("/products?limit=200").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const parseImages = (raw) =>
    (raw || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/products", {
        ...form,
        slug: form.name.toLowerCase().replace(/ /g, "-"),
        compare_at_price: form.compare_at_price || null,
        video_url: form.video_url || null,
        images: parseImages(form.images),
        sizes: form.sizes || [],
        variants: form.variants || [],
      });
      toast.success("Product added");
      setShowNew(false);
      setForm(EMPTY_PRODUCT);
      load();
    } catch (err) { toast.error("Failed to create"); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      category: p.category,
      fabric: p.fabric,
      color: p.color,
      price: p.price,
      compare_at_price: p.compare_at_price || 0,
      description: p.description,
      images: (p.images || []).join("\n"),
      video_url: p.video_url || "",
      sizes: p.sizes || [],
      stock: p.stock,
      featured: !!p.featured,
      new_arrival: !!p.new_arrival,
      variants: p.variants || [],
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/products/${editing.product_id}`, {
        name: form.name,
        category: form.category,
        fabric: form.fabric,
        color: form.color,
        price: form.price,
        compare_at_price: form.compare_at_price || null,
        description: form.description,
        stock: form.stock,
        featured: form.featured,
        new_arrival: form.new_arrival,
        images: parseImages(form.images),
        video_url: form.video_url || null,
        sizes: form.sizes || [],
        variants: form.variants || [],
      });
      toast.success("Product updated");
      setEditing(null);
      setForm(EMPTY_PRODUCT);
      load();
    } catch (err) { toast.error("Failed to update"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-serif text-3xl text-[#2b1b1c]">Products</h1>
          <p className="text-[#7c7273] text-sm mt-1">Manage inventory, prices, images and visibility.</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => { setShowNew(!showNew); setForm(EMPTY_PRODUCT); }}
          data-testid="admin-new-product-btn"
        >
          {showNew ? "Close" : "+ Add Product"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="bg-white border border-[#e8e4db] p-6 mb-6" data-testid="admin-new-product-form">
          <ProductForm value={form} onChange={setForm} />
          <div className="mt-4 flex gap-3 justify-end">
            <button type="button" className="btn-outline" onClick={() => { setShowNew(false); setForm(EMPTY_PRODUCT); }}>Cancel</button>
            <button type="submit" className="btn-primary" data-testid="admin-form-submit">Create Product</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-[#e8e4db] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f5f3ef] text-left">
            <tr>
              <th className="p-3">Product</th>
              <th className="p-3">Category</th>
              <th className="p-3">Price</th>
              <th className="p-3">Stock</th>
              <th className="p-3">Visibility</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.product_id} className="border-t border-[#e8e4db]" data-testid={`admin-product-${p.product_id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <img src={imgUrl(p.images[0])} alt="" className="w-10 h-12 object-cover bg-[#f5f3ef]" />
                    <span className="font-serif">{p.name}</span>
                  </div>
                </td>
                <td className="p-3">{p.category}</td>
                <td className="p-3 text-[#7a1e2b]">{formatINR(p.price)}</td>
                <td className={`p-3 ${p.stock === 0 ? "text-red-600 font-medium" : ""}`}>{p.stock}</td>
                <td className="p-3 text-xs">
                  {p.featured && <span className="mr-1 bg-[#c9a34a] text-white px-1.5 py-0.5">FEATURED</span>}
                  {p.new_arrival && <span className="bg-[#7a1e2b] text-white px-1.5 py-0.5">NEW</span>}
                </td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => openEdit(p)}
                    className="text-[#2b1b1c] hover:text-[#7a1e2b] mr-3 inline-flex items-center gap-1"
                    data-testid={`admin-edit-${p.product_id}`}
                  >
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    onClick={() => del(p.product_id)}
                    className="text-[#7a1e2b] hover:text-[#5c1520]"
                    data-testid={`admin-del-${p.product_id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(EMPTY_PRODUCT); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit Product</DialogTitle>
            <DialogDescription>Update inventory, price, images and details for {editing?.name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit}>
            <ProductForm value={form} onChange={setForm} />
            <DialogFooter className="mt-6">
              <button type="button" className="btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="admin-edit-save">Save Changes</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ORDER_STATUSES = ["pending", "confirmed", "packed", "shipped", "out_for_delivery", "delivered", "cancelled"];

function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [trackingInput, setTrackingInput] = useState({});

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status });
    toast.success(`Marked as ${status}`);
    load();
  };
  const saveTracking = async (id) => {
    const t = trackingInput[id] || {};
    if (!t.tracking_number && !t.courier) return toast.error("Enter courier or tracking number");
    await api.patch(`/admin/orders/${id}`, t);
    toast.success("Tracking saved");
    load();
  };

  return (
    <div>
      <h1 className="font-serif text-3xl text-[#2b1b1c] mb-6">Orders</h1>
      <div className="bg-white border border-[#e8e4db] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f5f3ef] text-left">
            <tr>
              <th className="p-3">Order</th>
              <th className="p-3">Date</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Total</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <React.Fragment key={o.order_id}>
                <tr className="border-t border-[#e8e4db]" data-testid={`admin-order-${o.order_id}`}>
                  <td className="p-3 font-mono text-xs">#{o.order_id.slice(-8).toUpperCase()}</td>
                  <td className="p-3">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="p-3">{o.address.full_name}</td>
                  <td className="p-3 text-[#7a1e2b]">{formatINR(o.total)}</td>
                  <td className="p-3 uppercase text-xs">{o.payment_method}</td>
                  <td className="p-3">
                    <Select value={o.status} onValueChange={(v) => setStatus(o.order_id, v)}>
                      <SelectTrigger className="w-40 h-8 text-xs" data-testid={`admin-order-status-${o.order_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3">
                    <button onClick={() => setExpanded(expanded === o.order_id ? null : o.order_id)} className="text-xs link-underline text-[#7a1e2b]" data-testid={`admin-order-expand-${o.order_id}`}>
                      {expanded === o.order_id ? "Close" : "Manage"}
                    </button>
                  </td>
                </tr>
                {expanded === o.order_id && (
                  <tr className="bg-[#f5f3ef]">
                    <td colSpan={7} className="p-4">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-xs uppercase tracking-widest text-[#7c7273] mb-2">Shipping to</h4>
                          <p className="text-sm">
                            {o.address.full_name}<br/>
                            {o.address.line1}{o.address.line2 ? `, ${o.address.line2}` : ""}<br/>
                            {o.address.city}, {o.address.state} - {o.address.pincode}<br/>
                            {o.address.phone}
                          </p>
                          <h4 className="text-xs uppercase tracking-widest text-[#7c7273] mt-4 mb-2">Items ({o.items.length})</h4>
                          <ul className="text-sm space-y-1">
                            {o.items.map((it) => <li key={`${it.product_id}::${it.variant_id || "base"}`}>{it.name}{it.color ? ` · ${it.color}` : ""} × {it.quantity}</li>)}
                          </ul>
                        </div>
                        <div>
                          <h4 className="text-xs uppercase tracking-widest text-[#7c7273] mb-2">Tracking</h4>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Courier (e.g. Delhivery)"
                              defaultValue={o.courier || ""}
                              onChange={(e) => setTrackingInput({ ...trackingInput, [o.order_id]: { ...trackingInput[o.order_id], courier: e.target.value } })}
                              data-testid={`admin-track-courier-${o.order_id}`}
                            />
                            <Input
                              placeholder="AWB / Tracking #"
                              defaultValue={o.tracking_number || ""}
                              onChange={(e) => setTrackingInput({ ...trackingInput, [o.order_id]: { ...trackingInput[o.order_id], tracking_number: e.target.value } })}
                              data-testid={`admin-track-number-${o.order_id}`}
                            />
                            <button onClick={() => saveTracking(o.order_id)} className="btn-primary text-xs py-2 px-4" data-testid={`admin-track-save-${o.order_id}`}>Save</button>
                          </div>
                          <h4 className="text-xs uppercase tracking-widest text-[#7c7273] mt-4 mb-2">Status History</h4>
                          <ol className="text-sm space-y-1">
                            {(o.status_history || []).map((h, i) => (
                              <li key={i} className="flex justify-between border-b border-[#e8e4db] py-1">
                                <span className="capitalize">{h.status.replace(/_/g, " ")}</span>
                                <span className="text-[#7c7273] text-xs">{new Date(h.at).toLocaleString()}</span>
                              </li>
                            ))}
                            {(!o.status_history || o.status_history.length === 0) && <li className="text-[#7c7273] text-xs">No history yet</li>}
                          </ol>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {orders.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#7c7273] font-italic-serif">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CoverUploader({ value, onChange }) {
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  const upload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image exceeds 10 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Cover Image</Label>
      <div className="flex items-start gap-4">
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
          className="w-40 h-52 border-2 border-dashed border-[#e8e4db] hover:border-[#c9a34a] cursor-pointer bg-[#f5f3ef] flex items-center justify-center overflow-hidden relative"
          data-testid="cat-cover-dropzone"
        >
          {value ? (
            <img src={toFullUrl(value)} alt="cover" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.2; }} />
          ) : (
            <div className="text-center px-2">
              <Upload size={22} className="mx-auto text-[#c9a34a] mb-1" />
              <div className="text-xs text-[#7c7273]">{uploading ? "Uploading…" : "Drop or click to upload"}</div>
            </div>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files)} data-testid="cat-cover-input" />
        <div className="flex-1">
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or paste a cover image URL"
            className="text-sm"
            data-testid="cat-cover-url"
          />
          <p className="text-[11px] text-[#7c7273] mt-1.5">JPEG, PNG or WebP up to 10 MB. 3:4 portrait crops look best.</p>
        </div>
      </div>
    </div>
  );
}

function CategoryForm({ value, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Name *</Label>
          <Input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder="e.g. Cotton" data-testid="cat-name" required />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Sort Order</Label>
          <Input type="number" value={value.sort_order} onChange={(e) => onChange({ ...value, sort_order: parseInt(e.target.value || "0", 10) })} data-testid="cat-order" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Tagline</Label>
        <Input value={value.tagline} onChange={(e) => onChange({ ...value, tagline: e.target.value })} placeholder="Everyday elegance" data-testid="cat-tagline" />
      </div>
      <CoverUploader value={value.cover_image} onChange={(v) => onChange({ ...value, cover_image: v })} />
      <div className="flex items-center gap-3 pt-2">
        <Switch checked={!value.hidden} onCheckedChange={(v) => onChange({ ...value, hidden: !v })} data-testid="cat-visible-toggle" />
        <span className="text-sm text-[#2b1b1c]">Visible on the landing page</span>
      </div>
    </div>
  );
}

const EMPTY_CATEGORY = { name: "", tagline: "", cover_image: "", sort_order: 0, hidden: false };

function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_CATEGORY);
  const [editing, setEditing] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/admin/categories", form);
      toast.success("Category added");
      setShowNew(false);
      setForm(EMPTY_CATEGORY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/categories/${editing.category_id}`, form);
      toast.success("Category updated");
      setEditing(null);
      setForm(EMPTY_CATEGORY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Remove "${c.name}" from the landing page?`)) return;
    try {
      await api.delete(`/admin/categories/${c.category_id}`);
      toast.success("Category removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const toggleHidden = async (c) => {
    try {
      await api.patch(`/admin/categories/${c.category_id}`, { hidden: !c.hidden });
      load();
    } catch (err) {
      toast.error("Could not update visibility");
    }
  };

  const openEdit = (c) => {
    setForm({ name: c.name, tagline: c.tagline || "", cover_image: c.cover_image || "", sort_order: c.sort_order || 0, hidden: !!c.hidden });
    setEditing(c);
  };

  const onDrop = async (targetIdx) => {
    if (dragIndex === null || dragIndex === targetIdx) return;
    const reordered = [...cats];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIdx, 0, moved);
    // Re-assign sort_order starting at 1
    const payload = reordered.map((c, i) => ({ category_id: c.category_id, sort_order: i + 1 }));
    setCats(reordered.map((c, i) => ({ ...c, sort_order: i + 1 })));
    setDragIndex(null);
    try {
      await api.post("/admin/categories/reorder", payload);
      toast.success("Order saved");
    } catch (err) {
      toast.error("Could not save order");
      load();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-serif text-3xl text-[#2b1b1c]">Landing Categories</h1>
          <p className="text-[#7c7273] text-sm mt-1">Drag rows to reorder. Categories with zero in-stock products are auto-hidden from the storefront.</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => { setShowNew(!showNew); setForm(EMPTY_CATEGORY); }}
          data-testid="admin-new-category-btn"
        >
          {showNew ? "Close" : "+ Add Category"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="bg-white border border-[#e8e4db] p-6 mb-6" data-testid="admin-new-category-form">
          <CategoryForm value={form} onChange={setForm} />
          <div className="mt-6 flex gap-3 justify-end">
            <button type="button" className="btn-outline" onClick={() => { setShowNew(false); setForm(EMPTY_CATEGORY); }}>Cancel</button>
            <button type="submit" className="btn-primary" data-testid="admin-category-submit">Add Category</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-[#e8e4db]">
        {cats.length === 0 ? (
          <div className="p-12 text-center text-[#7c7273] font-italic-serif">No categories yet — add your first weave tile above.</div>
        ) : (
          <ul className="divide-y divide-[#e8e4db]" data-testid="admin-categories-list">
            {cats.map((c, idx) => (
              <li
                key={c.category_id}
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(idx)}
                onDragEnd={() => setDragIndex(null)}
                className={`flex items-center gap-4 p-4 cursor-move transition-colors ${dragIndex === idx ? "opacity-40 bg-[#f5f3ef]" : "hover:bg-[#faf8f4]"}`}
                data-testid={`admin-category-${c.name}`}
              >
                <GripVertical size={16} className="text-[#c9a34a] flex-shrink-0" />
                <div className="w-16 h-20 bg-[#f5f3ef] overflow-hidden flex-shrink-0">
                  {c.cover_image ? (
                    <img src={toFullUrl(c.cover_image)} alt={c.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.2; }} />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-serif text-lg text-[#2b1b1c]">{c.name}</div>
                    {c.product_count === 0 && (
                      <span className="text-[10px] uppercase tracking-widest bg-[#f2c94c]/25 text-[#8a6d0b] px-1.5 py-0.5" title="Zero in-stock products; tile auto-hidden on storefront">
                        Empty · auto-hidden
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-[#7c7273] truncate">{c.tagline || <span className="italic">no tagline</span>}</div>
                  <div className="text-[11px] uppercase tracking-widest text-[#7c7273] mt-1">Order · {c.sort_order} · {c.product_count} product{c.product_count === 1 ? "" : "s"}</div>
                </div>
                <button
                  onClick={() => toggleHidden(c)}
                  className={`text-xs px-2 py-1 border ${c.hidden ? "border-[#e8e4db] text-[#7c7273]" : "border-[#4a7a59] text-[#4a7a59]"}`}
                  title={c.hidden ? "Hidden from landing page" : "Visible on landing page"}
                  data-testid={`admin-category-visibility-${c.name}`}
                >
                  {c.hidden ? <><EyeOff size={12} className="inline mr-1" />Hidden</> : <><Eye size={12} className="inline mr-1" />Visible</>}
                </button>
                <button
                  onClick={() => openEdit(c)}
                  className="text-[#2b1b1c] hover:text-[#7a1e2b] inline-flex items-center gap-1 text-sm"
                  data-testid={`admin-category-edit-${c.name}`}
                >
                  <Pencil size={14} /> Edit
                </button>
                <button
                  onClick={() => del(c)}
                  className="text-[#7a1e2b] hover:text-[#5c1520] p-1"
                  data-testid={`admin-category-del-${c.name}`}
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(EMPTY_CATEGORY); } }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-white" data-testid="admin-category-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit Category</DialogTitle>
            <DialogDescription>Update the name, tagline, cover image or ordering for “{editing?.name}”.</DialogDescription>
          </DialogHeader>
          <CategoryForm value={form} onChange={setForm} />
          <DialogFooter>
            <button className="btn-outline" onClick={() => { setEditing(null); setForm(EMPTY_CATEGORY); }}>Cancel</button>
            <button className="btn-primary" onClick={saveEdit} data-testid="admin-category-save">Save changes</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SalesAdmin() {
  const [sales, setSales] = useState([]);
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("");
  const [discount, setDiscount] = useState(20);
  const [endsAt, setEndsAt] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/sales").then((r) => setSales(r.data)).catch(() => {});
    api.get("/admin/categories").then((r) => {
      setCats(r.data);
      if (r.data.length && !category) setCategory(r.data[0].name);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!category) return toast.error("Pick a category");
    if (!endsAt) return toast.error("Pick an end date");
    if (discount <= 0 || discount >= 100) return toast.error("Discount must be between 1 and 99");
    setSaving(true);
    try {
      await api.post("/admin/sales", { category, discount_percent: parseFloat(discount), ends_at: endsAt });
      toast.success(`${discount}% off ${category} — live now`);
      setEndsAt("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create sale");
    } finally {
      setSaving(false);
    }
  };

  const del = async (s) => {
    if (!window.confirm(`End the ${s.discount_percent}% sale on ${s.category}?`)) return;
    try {
      await api.delete(`/admin/sales/${s.sale_id}`);
      toast.success("Sale ended");
      load();
    } catch (err) {
      toast.error("Could not end sale");
    }
  };

  const now = new Date();
  const active = sales.filter((s) => new Date(s.ends_at) > now);
  const past = sales.filter((s) => new Date(s.ends_at) <= now);

  const todayISO = new Date().toISOString().split("T")[0];

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-3xl text-[#2b1b1c]">Category Sales</h1>
        <p className="text-[#7c7273] text-sm mt-1">Put a whole category on sale with one click. Sale prices are computed live — no product edits, and everything auto-reverts on the end date.</p>
      </div>

      {/* Create form */}
      <div className="bg-white border border-[#e8e4db] p-6 mb-8" data-testid="admin-new-sale-form">
        <h2 className="font-serif text-xl text-[#2b1b1c] mb-4">Start a new sale</h2>
        <div className="grid sm:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-none bg-white" data-testid="sale-category">
                <SelectValue placeholder="Pick a category" />
              </SelectTrigger>
              <SelectContent>
                {cats.map((c) => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Discount (%)</Label>
            <Input
              type="number"
              min="1"
              max="99"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              data-testid="sale-discount"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Ends on</Label>
            <Input
              type="date"
              min={todayISO}
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              data-testid="sale-ends-at"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={create}
              disabled={saving}
              className="btn-primary w-full"
              data-testid="sale-create-btn"
            >
              {saving ? "Saving…" : "Start sale"}
            </button>
          </div>
        </div>
        <p className="text-[11px] text-[#7c7273] mt-3">Only one active sale is allowed per category — creating a new one for the same category ends the previous one.</p>
      </div>

      {/* Active sales */}
      <div className="mb-8">
        <h2 className="font-serif text-xl text-[#2b1b1c] mb-3">Live now ({active.length})</h2>
        {active.length === 0 ? (
          <div className="bg-white border border-dashed border-[#e8e4db] p-8 text-center text-[#7c7273] font-italic-serif">
            No live sales right now.
          </div>
        ) : (
          <ul className="bg-white border border-[#e8e4db] divide-y divide-[#e8e4db]" data-testid="admin-active-sales-list">
            {active.map((s) => (
              <li key={s.sale_id} className="p-4 flex items-center gap-4" data-testid={`admin-sale-${s.category}`}>
                <span className="bg-[#7a1e2b] text-white text-xs tracking-widest uppercase px-3 py-1">{Math.round(s.discount_percent)}% OFF</span>
                <div className="flex-1">
                  <div className="font-serif text-lg text-[#2b1b1c]">{s.category}</div>
                  <div className="text-xs text-[#7c7273]">Ends {new Date(s.ends_at).toLocaleString()}</div>
                </div>
                <button onClick={() => del(s)} className="text-[#7a1e2b] hover:text-[#5c1520] text-sm inline-flex items-center gap-1" data-testid={`sale-end-${s.category}`}>
                  <X size={14} /> End sale
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Past sales */}
      {past.length > 0 && (
        <div>
          <h2 className="font-serif text-xl text-[#2b1b1c] mb-3">Past sales</h2>
          <ul className="bg-white border border-[#e8e4db] divide-y divide-[#e8e4db] text-sm">
            {past.map((s) => (
              <li key={s.sale_id} className="p-3 flex items-center gap-3 text-[#7c7273]">
                <span className="text-xs">{Math.round(s.discount_percent)}%</span>
                <span className="flex-1">{s.category}</span>
                <span className="text-xs">ended {new Date(s.ends_at).toLocaleDateString()}</span>
                <button onClick={() => del(s)} className="hover:text-[#7a1e2b] p-1"><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SettingsAdmin() {
  const [notifyEmails, setNotifyEmails] = useState("");
  const [codEnabled, setCodEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCod, setSavingCod] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => {
        setNotifyEmails(r.data?.admin_notify_emails || "");
        const cod = r.data?.cod_enabled;
        setCodEnabled(cod === undefined ? true : (cod === true || cod === "true" || cod === 1));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const parsed = notifyEmails.split(",").map((s) => s.trim()).filter(Boolean);
  const invalid = parsed.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

  const save = async () => {
    if (invalid.length) return toast.error(`Fix these: ${invalid.join(", ")}`);
    setSaving(true);
    try {
      await api.patch("/admin/settings", { admin_notify_emails: parsed.join(",") });
      toast.success(parsed.length ? `New-order alerts will go to ${parsed.length} recipient${parsed.length > 1 ? "s" : ""}` : "Notification emails cleared");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleCod = async (next) => {
    setCodEnabled(next);
    setSavingCod(true);
    try {
      await api.patch("/admin/settings", { cod_enabled: next });
      toast.success(next ? "Cash on Delivery enabled" : "Cash on Delivery disabled — only online payments will show at checkout");
    } catch (err) {
      setCodEnabled(!next);
      toast.error("Could not save");
    } finally {
      setSavingCod(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="font-serif text-3xl text-[#2b1b1c]">Settings</h1>
        <p className="text-[#7c7273] text-sm mt-1">Storefront notifications and admin preferences.</p>
      </div>

      {/* COD toggle */}
      <div className="bg-white border border-[#e8e4db] p-6 mb-6" data-testid="admin-settings-cod-block">
        <h2 className="font-serif text-xl text-[#2b1b1c]">Cash on Delivery</h2>
        <p className="text-sm text-[#7c7273] mt-1">Turn off to hide the COD option from checkout — only Razorpay online payment will show.</p>
        <div className="mt-5 flex items-center gap-3">
          <Switch checked={codEnabled} disabled={savingCod || loading} onCheckedChange={toggleCod} data-testid="settings-cod-toggle" />
          <span className="text-sm text-[#2b1b1c]">{codEnabled ? "Enabled — shoppers can pick Cash on Delivery" : "Disabled — checkout accepts online payment only"}</span>
        </div>
      </div>

      <div className="bg-white border border-[#e8e4db] p-6" data-testid="admin-settings-notify-block">
        <h2 className="font-serif text-xl text-[#2b1b1c]">New-order email alerts</h2>
        <p className="text-sm text-[#7c7273] mt-1">The people below get an email the moment a customer places an order. Add up to a few teammates — comma-separated.</p>

        {loading ? (
          <div className="mt-6 text-sm text-[#7c7273]">Loading…</div>
        ) : (
          <>
            <div className="mt-6">
              <Label className="text-xs uppercase tracking-widest text-[#7c7273] mb-1.5 block">Recipients</Label>
              <Textarea
                value={notifyEmails}
                onChange={(e) => setNotifyEmails(e.target.value)}
                placeholder="you@sareesbyraunak.com, team@sareesbyraunak.com"
                rows={2}
                className="rounded-none"
                data-testid="settings-notify-emails"
              />
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {parsed.length === 0 && (
                  <span className="text-[11px] text-[#7c7273] italic">No recipients — alerts will fall back to the admin login email.</span>
                )}
                {parsed.map((e, i) => (
                  <span key={i} className={`text-[11px] px-2 py-1 border ${invalid.includes(e) ? "border-[#b33939] text-[#b33939]" : "border-[#e8e4db] text-[#2b1b1c] bg-[#faf8f4]"}`}>{e}</span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={save}
                disabled={saving || invalid.length > 0}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="settings-save-btn"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {invalid.length > 0 && (
                <span className="text-xs text-[#b33939]">Fix invalid addresses to save.</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      toast.error("Admin access required");
      nav("/login");
    }
  }, [user, loading, nav]);

  if (loading || !user || user.role !== "admin") return <div className="py-24 text-center text-[#7c7273]">Loading…</div>;

  return (
    <div className="flex bg-[#fcfbf8]" data-testid="admin-dashboard">
      <Sidebar />
      <div className="flex-1 p-8 min-h-screen">
        <Routes>
          <Route index element={<Stats />} />
          <Route path="products" element={<ProductsAdmin />} />
          <Route path="categories" element={<CategoriesAdmin />} />
          <Route path="sales" element={<SalesAdmin />} />
          <Route path="orders" element={<OrdersAdmin />} />
          <Route path="settings" element={<SettingsAdmin />} />
        </Routes>
      </div>
    </div>
  );
}
